import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, MapPin, Phone, Mail, GraduationCap, Ship, Award, ArrowRight, Clock, Sparkles } from 'lucide-react';

// Assets
import bcSkyline from '@/assets/bc-skyline.jpg';

// Infographic components
import EcossistemaNauticoSection from '@/components/balneario/EcossistemaNauticoSection';
import DestinosSection from '@/components/balneario/DestinosSection';
import NumerosBCSection from '@/components/balneario/NumerosBCSection';


// =====================================================
// 1. HERO BC (Redesigned)
// =====================================================
const HeroBC: React.FC = () => (
    <section className="relative min-h-[85vh] flex items-center justify-center text-center text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
            <img src={bcSkyline} alt="Skyline de Balneário Camboriú com marina" className="w-full h-full object-cover scale-105" />
        </div>
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-primary/80 via-primary/50 to-primary/90" />
        <div
            className="absolute inset-0 z-10 opacity-[0.03] pointer-events-none"
            style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                backgroundSize: '32px 32px',
            }}
        />

        <div className="relative z-20 container mx-auto px-6">
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <div className="inline-flex items-center gap-2 px-5 py-2 mb-8 rounded-full bg-gold/20 text-gold font-bold tracking-wider text-sm border border-gold/30">
                    <MapPin className="w-4 h-4" />
                    UNIDADE BALNEÁRIO CAMBORIÚ
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight max-w-5xl mx-auto">
                    Sua Jornada Náutica Começa em <span className="text-gold">Balneário Camboriú</span>
                </h1>
                <p className="mt-6 text-lg md:text-xl max-w-3xl mx-auto text-white/80 font-light leading-relaxed">
                    A capital náutica de Santa Catarina. Navegue pelas águas mais desejadas do Sul do Brasil com a excelência e tradição da JL Escola Náutica.
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                    <a href="#nossos-servicos">
                        <Button size="lg" className="btn-gold w-full sm:w-auto text-lg px-8 py-6">
                            Descubra Nossos Serviços
                        </Button>
                    </a>
                    <a href="#contato-bc">
                        <Button size="lg" variant="outline" className="w-full sm:w-auto border-gold text-gold hover:bg-gold/10 hover:text-gold">
                            Fale Conosco
                        </Button>
                    </a>
                </div>
            </motion.div>

            {/* Stats Strip */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-16"
            >
                {[
                    { value: '561 km', label: 'Costa Catarinense' },
                    { value: '280+', label: 'Dias Navegáveis' },
                    { value: '15+', label: 'Destinos Próximos' },
                    { value: '4.9 ★', label: 'Avaliação Google' },
                ].map((stat) => (
                    <div
                        key={stat.label}
                        className="bg-white/5 backdrop-blur-sm p-5 rounded-xl border border-white/10 hover:border-gold/30 transition-all duration-300"
                    >
                        <div className="text-3xl font-bold text-gold">{stat.value}</div>
                        <div className="text-xs text-white/60 uppercase tracking-wider mt-1">{stat.label}</div>
                    </div>
                ))}
            </motion.div>
        </div>
    </section>
);


// =====================================================
// 2. WELCOME SECTION (Enhanced)
// =====================================================
const WelcomeBCSection: React.FC = () => (
    <section className="py-24 sm:py-32 bg-background">
        <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
            >
                <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-6">
                    Bem-vindo
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary mb-6">
                    A Excelência JL Agora no Coração de BC
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                    Balneário Camboriú não é apenas uma cidade à beira-mar — é o <strong className="text-primary">epicentro da vida náutica</strong> catarinense. Aqui, ilhas paradisíacas estão a minutos de barco, marinas de classe internacional recebem embarcações de todos os portes, e o mar é o quintal de quem entende de qualidade de vida.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                    A JL Escola Náutica escolheu BC para sua unidade estratégica porque acredita que <strong className="text-primary">o melhor do mar merece o melhor em formação, documentação e consultoria</strong>. Seja você morador, veranista ou turista, a JL é sua porta de entrada para navegar com segurança, legalidade e prazer.
                </p>
                <div className="flex flex-wrap gap-3">
                    {['Escola Náutica', 'Despachante', 'Consultoria Premium'].map(tag => (
                        <span key={tag} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/5 border border-primary/10 rounded-full text-sm font-medium text-primary">
                            <Check className="w-3.5 h-3.5 text-gold" />
                            {tag}
                        </span>
                    ))}
                </div>
            </motion.div>
            <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
                className="relative"
            >
                <div
                    className="absolute -inset-4 rounded-2xl pointer-events-none animate-glow-pulse opacity-40"
                    style={{ boxShadow: '0 0 60px hsl(200 70% 50% / 0.1)' }}
                />
                <img
                    src="/assets/BC.jpeg"
                    alt="Unidade JL Escola Náutica Balneário Camboriú"
                    className="rounded-xl shadow-2xl relative z-10 w-full"
                />
                <div className="absolute top-4 left-4 z-20 bg-gold/90 backdrop-blur-sm text-primary text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                    📍 Jardim Iate Clube
                </div>
            </motion.div>
        </div>
    </section>
);


// =====================================================
// 3. SERVICES TABS (Enhanced)
// =====================================================
const ServicesBCSection: React.FC = () => (
    <section id="nossos-servicos" className="py-24 sm:py-32 bg-primary/[0.03]">
        <div className="container mx-auto px-6">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
            >
                <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                    Serviços
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary">
                    Tudo que Você Precisa para o Mar
                </h2>
                <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
                    Da habilitação à gestão completa da sua embarcação — nossa unidade BC oferece a gama completa de soluções JL.
                </p>
            </motion.div>

            <Tabs defaultValue="escola" className="w-full max-w-4xl mx-auto">
                <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 bg-cream border border-gold/20">
                    <TabsTrigger value="escola"><GraduationCap className="w-5 h-5 mr-2" /> Escola Náutica</TabsTrigger>
                    <TabsTrigger value="despachante"><Ship className="w-5 h-5 mr-2" /> Despachante</TabsTrigger>
                    <TabsTrigger value="premium"><Award className="w-5 h-5 mr-2" /> Consultoria Premium</TabsTrigger>
                </TabsList>

                <TabsContent value="escola" className="py-8 bg-white p-8 rounded-b-lg shadow-md border border-t-0 border-border/30">
                    <h3 className="font-heading text-2xl text-primary mb-4">Habilite-se e Explore as Belezas de BC!</h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                        Nossos cursos de Arrais Amador, Motonauta e Mestre Amador estão disponíveis na unidade de Balneário Camboriú. Aprenda com instrutores que conhecem cada canto da costa catarinense — da Ilha das Cabras a Bombinhas.
                    </p>
                    <ul className="space-y-3 mb-6 text-primary/90">
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Aulas práticas em embarcações modernas nas águas de BC.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Instrutores que conhecem a fundo a costa catarinense.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Metodologia focada na sua segurança e confiança ao pilotar.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Turmas reduzidas para atenção personalizada.</li>
                    </ul>
                    <a href="/escola">
                        <Button className="btn-gold">Ver Detalhes dos Cursos <ArrowRight className="ml-2 w-4 h-4" /></Button>
                    </a>
                </TabsContent>

                <TabsContent value="despachante" className="py-8 bg-white p-8 rounded-b-lg shadow-md border border-t-0 border-border/30">
                    <h3 className="font-heading text-2xl text-primary mb-4">Despachante Náutico: Burocracia Zero em BC</h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                        BC é uma cidade com grande movimentação náutica e a regularização de embarcações exige atenção. Nosso despachante especializado cuida de tudo, para que você aproveite o mar sem preocupações documentais.
                    </p>
                    <ul className="space-y-3 mb-6 text-primary/90">
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Inscrição e Registro de Embarcações.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Transferência de Propriedade ágil e segura.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Regularização junto à ANATEL e órgãos competentes.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Acompanhamento de vencimentos e renovações.</li>
                    </ul>
                    <a href="/despachante">
                        <Button className="btn-gold">Saiba Mais sobre Despachante <ArrowRight className="ml-2 w-4 h-4" /></Button>
                    </a>
                </TabsContent>

                <TabsContent value="premium" className="py-8 bg-white p-8 rounded-b-lg shadow-md border border-t-0 border-border/30">
                    <h3 className="font-heading text-2xl text-primary mb-4">Consultoria Premium em Balneário Camboriú</h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                        Para quem busca exclusividade e o mais alto nível de serviço. Aquisição de embarcações com vistoria completa, habilitação express com agenda VIP, e gestão náutica 360° na região de Balneário Camboriú.
                    </p>
                    <ul className="space-y-3 mb-6 text-primary/90">
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Aquisição segura com vistoria técnica completa.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Treinamento VIP a bordo da sua própria embarcação.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Concierge náutico para gestão total da sua vida no mar.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Consultor dedicado 24/7 para qualquer necessidade.</li>
                    </ul>
                    <a href="/consultoria-premium">
                        <Button className="btn-gold">Descobrir Consultoria Premium <ArrowRight className="ml-2 w-4 h-4" /></Button>
                    </a>
                </TabsContent>
            </Tabs>
        </div>
    </section>
);


// =====================================================
// 4. WHY JL IN BC (Enhanced)
// =====================================================
const WhyJLBCSection: React.FC = () => {
    const reasons = [
        {
            icon: <MapPin className="w-8 h-8 text-gold" />,
            title: "Localização Estratégica",
            description: "No Jardim Iate Clube, a metros das principais marinas. Acesso direto à R. Dom Henrique — o coração náutico de BC."
        },
        {
            icon: <GraduationCap className="w-8 h-8 text-gold" />,
            title: "Instrutores que Conhecem o Mar de BC",
            description: "Nossa equipe navega estas águas diariamente. Ensinamos não só a técnica, mas as particularidades locais: correntes, ventos e rotas seguras."
        },
        {
            icon: <Award className="w-8 h-8 text-gold" />,
            title: "Padrão de Excelência JL",
            description: "7 anos de tradição e 2.500+ alunos formados. Toda essa qualidade agora adaptada ao paraíso náutico de BC."
        },
        {
            icon: <Clock className="w-8 h-8 text-gold" />,
            title: "Horários Flexíveis",
            description: "Atendimento de segunda a sábado com agenda flexível. Cursos noturnos e práticas aos finais de semana sob demanda."
        }
    ];

    return (
        <section className="py-24 sm:py-32 bg-background">
            <div className="container mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                        Diferenciais
                    </span>
                    <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary">
                        Por Que Escolher a JL em BC?
                    </h2>
                    <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                        Não somos apenas uma escola náutica. Somos seu parceiro completo no mar.
                    </p>
                </motion.div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                    {reasons.map((reason, i) => (
                        <motion.div
                            key={reason.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            whileHover={{ scale: 1.03, y: -5 }}
                            className="text-center p-8 border border-border rounded-xl bg-white hover:shadow-xl hover:border-gold/30 transition-all duration-300 cursor-default"
                        >
                            <div className="flex justify-center mb-4">
                                <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center">
                                    {reason.icon}
                                </div>
                            </div>
                            <h3 className="text-lg font-bold text-primary mb-2">{reason.title}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">{reason.description}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};


// =====================================================
// 5. LOCATION SECTION (Enhanced)
// =====================================================
const LocationBCSection: React.FC = () => (
    <section className="py-24 sm:py-32 bg-primary/[0.03]">
        <div className="container mx-auto px-6">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
            >
                <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                    Localização
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary">Nossa Unidade em BC</h2>
                <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                    Venha nos conhecer. Estamos estrategicamente posicionados no Jardim Iate Clube.
                </p>
            </motion.div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-5xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3554.4702055711!2d-48.6322604!3d-27.015304099999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94d8b71d5bf80a2b%3A0x3c520e49d7133a!2sJL%20Despachante%20e%20Escola%20N%C3%A1utica!5e0!3m2!1spt-BR!2sbr!4v1771013852042!5m2!1spt-BR!2sbr"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        className="w-full h-80 rounded-xl shadow-lg grayscale hover:grayscale-0 transition-all duration-500 ease-in-out border border-border"
                    />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-white p-8 rounded-xl shadow-lg border border-border"
                >
                    <h3 className="font-heading text-2xl text-primary mb-6">Endereço e Contato</h3>
                    <div className="space-y-5 text-primary/90">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-5 h-5 text-gold" />
                            </div>
                            <div>
                                <p className="font-semibold">Endereço</p>
                                <p className="text-muted-foreground">R. Dom Henrique, 1200 - Jardim Iate Clube</p>
                                <p className="text-muted-foreground">Balneário Camboriú, SC</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                                <Phone className="w-5 h-5 text-gold" />
                            </div>
                            <div>
                                <p className="font-semibold">Telefone / WhatsApp</p>
                                <p className="text-muted-foreground">(47) 99146-8851</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                                <Mail className="w-5 h-5 text-gold" />
                            </div>
                            <div>
                                <p className="font-semibold">Email</p>
                                <p className="text-muted-foreground">jlescola.analitica@gmail.com</p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-border">
                        <h4 className="font-semibold text-primary mb-2">Horário de Funcionamento</h4>
                        <div className="space-y-1 text-sm text-muted-foreground">
                            <p>Segunda a Sexta: <span className="font-medium text-primary">09h00 - 18h00</span></p>
                            <p>Sábado: <span className="font-medium text-primary">09h00 - 13h00</span></p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
);


// =====================================================
// 6. CTA (Redesigned)
// =====================================================
const ContactBCSection: React.FC = () => (
    <section id="contato-bc" className="py-24 sm:py-32 bg-primary text-primary-foreground relative overflow-hidden">
        <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] pointer-events-none animate-glow-pulse"
            style={{
                background: 'radial-gradient(ellipse, hsl(200 70% 50% / 0.12), transparent 70%)',
                filter: 'blur(40px)',
            }}
        />

        <div className="container mx-auto px-6 text-center relative z-10">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
            >
                <Sparkles className="w-10 h-10 text-gold mx-auto mb-6" />
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mb-6">
                    Pronto para Navegar em <span className="text-gold">Balneário Camboriú</span>?
                </h2>
                <p className="max-w-2xl mx-auto text-primary-foreground/80 text-lg leading-relaxed mb-10">
                    O mar mais bonito do Sul do Brasil está esperando por você. Fale com nossa equipe e dê o primeiro passo rumo à sua liberdade náutica.
                </p>
                <a href="https://wa.me/5547991468851?text=Olá!%20Tenho%20interesse%20nos%20serviços%20da%20JL%20Escola%20Náutica%20em%20Balneário%20Camboriú." target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="btn-gold text-lg px-10 py-6">
                        Falar com a Unidade BC via WhatsApp
                    </Button>
                </a>
                <p className="text-sm text-primary-foreground/50 mt-6">
                    Atendimento rápido e personalizado. Resposta em até 2 horas.
                </p>
            </motion.div>
        </div>
    </section>
);


// =====================================================
// MAIN PAGE
// =====================================================
const BalnearioCamboriuPage: React.FC = () => {
    return (
        <>
            <Header />
            <main>
                <HeroBC />
                <WelcomeBCSection />
                <EcossistemaNauticoSection />
                <ServicesBCSection />
                <DestinosSection />
                <WhyJLBCSection />
                <NumerosBCSection />
                <LocationBCSection />
                <ContactBCSection />
            </main>
            <Footer />
        </>
    );
};

export default BalnearioCamboriuPage;
